/**
 * RfpFeedbackDialog - Captures user feedback on RFP edits for model training
 *
 * Shown after RFP download (on the completion view) to learn what users
 * typically change offline, or how they'd rate an unedited RFP.
 */
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Sparkles,
  Send,
  Loader2,
  Brain,
  CheckCircle2,
  Star
} from 'lucide-react';
import { toastSuccess, toastError, toastInfo } from '@/lib/toast';
import api from '@/lib/api';

const getDeviceType = () => {
  const width = window.innerWidth;
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobileUA = /mobile|android|iphone|ipad|tablet/i.test(userAgent);

  if (isMobileUA || width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

const SECTION_OPTIONS = [
  { id: 'introduction', label: 'Introduction' },
  { id: 'scope_of_work', label: 'Scope of Work' },
  { id: 'vendor_questions', label: 'Vendor Questions' },
  { id: 'line_items', label: 'Line Items / Pricing Table' },
  { id: 'submission_guidelines', label: 'Submission Guidelines' },
  { id: 'evaluation_criteria', label: 'Evaluation Criteria' },
];

const MIN_SECTION_DESCRIPTION_LENGTH = 15;

const RfpFeedbackDialog = ({
  open,
  onClose,
  procurementId,
  category,
  onSubmitComplete
}) => {
  const [madeChanges, setMadeChanges] = useState(null); // null | true | false
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [selectedSections, setSelectedSections] = useState([]);
  const [sectionChanges, setSectionChanges] = useState({});
  const [sectionErrors, setSectionErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [deviceType, setDeviceType] = useState('desktop');

  useEffect(() => {
    setDeviceType(getDeviceType());
  }, []);

  const resetState = () => {
    setMadeChanges(null);
    setRating(0);
    setHoverRating(0);
    setSelectedSections([]);
    setSectionChanges({});
    setSectionErrors({});
    setSubmitted(false);
  };

  const handleToggleSection = (sectionId) => {
    setSelectedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
    // Unticking a section should clear any stale error under it; reticking
    // starts clean too and gets re-validated on the next submit attempt.
    setSectionErrors(prev => {
      if (!(sectionId in prev)) return prev;
      const next = { ...prev };
      delete next[sectionId];
      return next;
    });
  };

  const handleSectionChangeText = (sectionId, text) => {
    setSectionChanges(prev => ({ ...prev, [sectionId]: text }));
    // Clear the error as soon as the user starts fixing it, rather than
    // waiting for the next submit attempt.
    setSectionErrors(prev => {
      if (!(sectionId in prev)) return prev;
      if (text.trim().length < MIN_SECTION_DESCRIPTION_LENGTH) return prev;
      const next = { ...prev };
      delete next[sectionId];
      return next;
    });
  };

  const canSubmit = madeChanges === false
    ? rating > 0
    : madeChanges === true
      ? selectedSections.length > 0
      : false;

  const handleSubmit = async () => {
    if (!canSubmit) {
      toastInfo(
        madeChanges === false
          ? 'Please select a rating'
          : 'Please select at least one section you changed'
      );
      return;
    }

    // Every ticked section needs a real description before this can submit -
    // block here and surface inline errors instead of silently substituting
    // a placeholder for whatever the user didn't type.
    if (madeChanges === true) {
      const errors = {};
      selectedSections.forEach((sectionId) => {
        const text = sectionChanges[sectionId] || '';
        if (text.trim().length < MIN_SECTION_DESCRIPTION_LENGTH) {
          errors[sectionId] = `Please briefly describe what changed — a few words at minimum — or untick this section`;
        }
      });
      if (Object.keys(errors).length > 0) {
        setSectionErrors(errors);
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        procurement_id: procurementId,
        category,
        made_changes: madeChanges,
        device_type: deviceType,
        screen_width: window.innerWidth,
      };

      if (madeChanges === false) {
        payload.rating = rating;
      } else {
        payload.sections = selectedSections.map(sectionId => ({
          field_name: sectionId,
          description: sectionChanges[sectionId].trim()
        }));
      }

      const response = await api.submitRfpFeedback(payload);

      setSubmitted(true);
      toastSuccess('Thank you! Your feedback helps improve our AI.');

      setTimeout(() => {
        if (onSubmitComplete) onSubmitComplete(response.data.rfp_version);
        onClose();
        resetState();
      }, 1500);
    } catch (error) {
      if (error.response?.status === 409) {
        toastInfo('Feedback already submitted for this RFP version');
        if (onSubmitComplete) onSubmitComplete();
        onClose();
        resetState();
      } else {
        console.error('Failed to submit feedback:', error);
        toastError('Failed to submit feedback');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    onClose();
    resetState();
  };

  if (submitted) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-zinc-900 mb-2">Thank You!</h3>
            <p className="text-sm text-zinc-600">
              Your feedback will help train our AI to generate better RFPs.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Brain className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <DialogTitle className="text-lg">Help Us Improve</DialogTitle>
              <DialogDescription className="text-sm">
                Quick feedback on your RFP
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-5 mt-4">
          {/* Opening message */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-purple-900">
                Thank you for using ProcMatrix and downloading your RFP. Once you have reviewed the document,
                we would love your feedback to help us improve.
              </p>
            </div>
          </div>

          {/* Q1: Did you make any changes? */}
          <div>
            <h4 className="text-sm font-medium text-zinc-900 mb-3">
              Did you make any changes to the RFP content after downloading?
            </h4>
            <div className="flex gap-3">
              <Button
                type="button"
                variant={madeChanges === true ? 'default' : 'outline'}
                className={madeChanges === true ? 'flex-1 bg-purple-600 hover:bg-purple-700 text-white' : 'flex-1'}
                onClick={() => setMadeChanges(true)}
                data-testid="feedback-made-changes-yes"
              >
                Yes
              </Button>
              <Button
                type="button"
                variant={madeChanges === false ? 'default' : 'outline'}
                className={madeChanges === false ? 'flex-1 bg-purple-600 hover:bg-purple-700 text-white' : 'flex-1'}
                onClick={() => setMadeChanges(false)}
                data-testid="feedback-made-changes-no"
              >
                No
              </Button>
            </div>
          </div>

          {/* If No: star rating */}
          {madeChanges === false && (
            <div>
              <h4 className="text-sm font-medium text-zinc-900 mb-3">
                How would you rate the quality of this RFP? (1 = Poor, 5 = Excellent)
              </h4>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-1"
                    data-testid={`feedback-star-${star}`}
                  >
                    <Star
                      className={`w-8 h-8 transition-colors ${
                        star <= (hoverRating || rating)
                          ? 'fill-amber-400 text-amber-400'
                          : 'text-zinc-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* If Yes: section checklist + per-section free text */}
          {madeChanges === true && (
            <div>
              <h4 className="text-sm font-medium text-zinc-900 mb-3">
                Which sections did you change?
              </h4>
              <div className="space-y-3">
                {SECTION_OPTIONS.map((option) => (
                  <div key={option.id}>
                    <div
                      onClick={() => handleToggleSection(option.id)}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedSections.includes(option.id)
                          ? 'bg-purple-50 border-purple-300'
                          : 'bg-white border-zinc-200 hover:border-zinc-300'
                      }`}
                    >
                      <Checkbox
                        checked={selectedSections.includes(option.id)}
                        onCheckedChange={() => handleToggleSection(option.id)}
                      />
                      <p className="text-sm font-medium text-zinc-900">{option.label}</p>
                    </div>
                    {selectedSections.includes(option.id) && (
                      <div className="mt-2 pl-3">
                        <label className="text-xs font-medium text-zinc-600 mb-1 block">
                          What did you change in {option.label}?
                        </label>
                        <Textarea
                          value={sectionChanges[option.id] || ''}
                          onChange={(e) => handleSectionChangeText(option.id, e.target.value)}
                          placeholder="Describe what you changed..."
                          className={`min-h-[60px] resize-none text-sm ${
                            sectionErrors[option.id] ? 'border-red-400 focus-visible:ring-red-400' : ''
                          }`}
                          data-testid={`feedback-section-text-${option.id}`}
                        />
                        {sectionErrors[option.id] && (
                          <p className="text-xs text-red-600 mt-1" data-testid={`feedback-section-error-${option.id}`}>
                            {sectionErrors[option.id]}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          {madeChanges !== null && (
            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleSubmit}
                disabled={submitting || !canSubmit}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="feedback-submit-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Submit Feedback
              </Button>
              <Button
                onClick={handleSkip}
                variant="ghost"
                className="text-zinc-500"
              >
                Skip
              </Button>
            </div>
          )}

          <p className="text-xs text-zinc-400 text-center">
            Feedback is anonymous and used only for improving RFP generation
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RfpFeedbackDialog;
